import { Interval, MetaTerms, Term } from "@/app/types"
import { FEATURE_FLAG_KEYS } from "@/app/types/flag_definitions"
import { isArrayWithAtleast } from "@/app/utils"
import { ItemGroup } from "@/components/selector/selector"
import { growthbook } from "@/lib/growthbook"

const TERM_ORDER = {
  daily: 1,
  monthly: 2,
  quarterly: 3,
  annual: 4,
}

/**
 * Generates term options based on the provided meta terms and conditions.
 *
 * @param isPeriodic - A boolean indicating whether the terms are periodic.
 * @param metaTerms - An object containing metadata about terms, including periodic and ranking terms.
 * @param isTermRelevant - A boolean indicating whether the term is relevant.
 * @param shouldDisplayOtherIntervals - A boolean indicating whether other intervals should be displayed. Defaults to true.
 * @returns An array of ItemGroup objects representing the term options, or undefined if no options are available.
 */
export function getTermOptions(
  term: Term,
  isPeriodic: boolean,
  metaTerms: MetaTerms,
  isTermRelevant: boolean,
  shouldDisplayOtherIntervals: boolean = true
): ItemGroup[] | undefined {
  const terms = isPeriodic ? metaTerms.periodic : metaTerms.ranking
  const names = terms.names || {}

  // calling growthbook to check if daily interval should be shown
  const shouldShowDailyInterval = growthbook.getFeatureValue(
    FEATURE_FLAG_KEYS.features.SHOW_DAILY_INTERVAL,
    false
  )

  const orderedIntervalKeys = Object.keys(terms).sort((a, b) => {
    return TERM_ORDER[a] - TERM_ORDER[b]
  })

  const orderedTerms = {}
  orderedIntervalKeys.forEach((key) => {
    orderedTerms[key] = terms[key]
  })

  if (!isPeriodic && !isTermRelevant) {
    return Object.entries(orderedTerms)
      .filter(([interval]) => {
        return shouldDisplayOtherIntervals ?
            interval !== "names" &&
              (shouldShowDailyInterval || interval !== "daily")
          : interval === term.interval
      })
      .map(([interval, intervalTerms]) => ({
        key: interval,
        disabled: false,
        text: names[interval]?.[2] || interval.toUpperCase(),
        items: (intervalTerms as Term[])
          .map((term, idx) => {
            if (idx < 1)
              return {
                key: term.key,
                infoText: null,
                disabled: false,
                text:
                  isArrayWithAtleast(term.name, 2) ? term.name[1] : term.key,
              }
          })
          .filter((item) => item),
      }))
  }

  return Object.entries(orderedTerms)
    .filter(([interval]) => {
      return shouldDisplayOtherIntervals ?
          interval !== "names" &&
            (shouldShowDailyInterval || interval !== "daily")
        : interval === term.interval
    })
    .map(([interval, intervalTerms]) => ({
      key: interval,
      disabled: false,
      text: names[interval]?.[1] || interval.toUpperCase(),
      items: (intervalTerms as Term[]).map((term) => ({
        key: term.key,
        infoText: null,
        disabled: false,
        text: isArrayWithAtleast(term.name, 2) ? term.name[1] : term.key,
      })),
    }))
}

/**
 * Generates term options for ranking intervals based on the provided meta terms and conditions.
 *
 * @param metaTerms - An object containing metadata about terms, including ranking terms.
 * @param isTermRelevant - A boolean indicating whether the term is relevant.
 * @param shouldDisplayOtherIntervals - A boolean indicating whether other intervals should be displayed. Defaults to true.
 * @returns An array of ItemGroup objects representing the term options, or undefined if no options are available.
 */
export function getRankingTermOptions(
  term: Term,
  metaTerms: MetaTerms,
  isTermRelevant: boolean,
  shouldDisplayOtherIntervals: boolean = true
): ItemGroup[] | undefined {
  const terms = metaTerms.ranking
  const names = terms.names || {}

  const isGlobalTermAnnual = term.interval === Interval.Annual

  // calling growthbook to check if daily interval should be shown
  const shouldShowDailyInterval = growthbook.getFeatureValue(
    FEATURE_FLAG_KEYS.features.SHOW_DAILY_INTERVAL,
    false
  )

  const orderedIntervalKeys = Object.keys(terms).sort((a, b) => {
    return TERM_ORDER[a] - TERM_ORDER[b]
  })

  const orderedTerms = {}
  orderedIntervalKeys.forEach((key) => {
    orderedTerms[key] = terms[key]
  })

  // returns term durations for interval in selector without accordion
  // annual terms are always relevant so they are excluded from here
  if (!isTermRelevant && !isGlobalTermAnnual) {
    return Object.entries(orderedTerms)
      .filter(([interval]) => {
        return shouldDisplayOtherIntervals ?
            interval !== "names" &&
              (shouldShowDailyInterval || interval !== "daily")
          : interval === term.interval
      })
      .map(([interval, intervalTerms]) => ({
        key: interval,
        disabled: false,
        text: names[interval]?.[2] || interval.toUpperCase(),
        items: (intervalTerms as Term[])
          .map((term, idx) => {
            if (idx < 1)
              return {
                key: term.key,
                infoText: null,
                disabled: false,
                text:
                  isArrayWithAtleast(term.name, 2) ? term.name[1] : term.key,
              }
          })
          .filter((item) => item),
      }))
  }

  // returns an accordion menu if it has more than one interval to be shown
  // used specifically to return annual term options for ranking values when global term is annual
  return Object.entries(orderedTerms)
    .filter(([interval]) => {
      return shouldDisplayOtherIntervals ?
          interval !== "names" &&
            (shouldShowDailyInterval || interval !== "daily")
        : interval === term.interval
    })
    .map(([interval, intervalTerms]) => ({
      key: interval,
      disabled: false,
      text: names[interval]?.[1] || interval.toUpperCase(),
      items: (intervalTerms as Term[]).map((term) => ({
        key: term.key,
        infoText: null,
        disabled: false,
        text: isArrayWithAtleast(term.name, 2) ? term.name[1] : term.key,
      })),
    }))
}

/**
 * Retrieves the key of the first term option that matches the given term's interval.
 *
 * @param termOptions - An array of ItemGroup objects representing the term options.
 * @param term - The term object containing the interval to match.
 * @returns An array containing the key of the first matching term option, or an empty array if no match is found.
 * @throws Will throw an error if any term option does not have at least one item.
 */
export function getTermOptionKey(termOptions: ItemGroup[], term: Term) {
  const termOptionItemsCheck = termOptions.every(
    (item) => item.items.length >= 1
  )

  if (!termOptionItemsCheck)
    throw new Error("Term options must have at least one item")

  const termOptionKey = termOptions
    .map((item) => {
      if (item.key === term.interval) {
        return item.items[0]?.key
      }
    })
    .filter((item) => item)
  return termOptionKey[0]
}
